import { parseContent, partLabel, safeStringify, uniqueParts } from '@/utils/traceContent'

export function formatTimestamp(timestamp: { seconds: bigint; nanos: number } | undefined) {
  if (!timestamp) return '-';
  const date = new Date(Number(timestamp.seconds) * 1000 + timestamp.nanos / 1000000);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function formatTimestampISO(timestamp: string | undefined) {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function formatLatency(latency: number | undefined) {
  if (latency === undefined || latency === 0) return '-';
  if (latency < 1000) return `${latency.toFixed(0)}ms`;
  if (latency < 60000) return `${(latency / 1000).toFixed(1)}s`;
  return `${(latency / 60000).toFixed(1)}m`;
}

export function parseMarkdown(text: string): string {
  let html = text
    .replace(/\\n/g, '\n')
    .replace(/\\u003e/g, '>')
    .replace(/\\u003c/g, '<')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\')
    .replace(
      /```(\w*)\n?([\s\S]*?)```/g,
      '<pre class="bg-muted rounded p-3 my-3 overflow-x-auto text-sm"><code>$2</code></pre>'
    )
    .replace(
      /`([^`]+)`/g,
      '<code class="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">$1</code>'
    )
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')
    .replace(/^### (.+)$/gm, '<h3 class="text-sm font-semibold mt-4 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-base font-semibold mt-4 mb-2">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-lg font-bold mt-4 mb-2">$1</h1>')
    .replace(/^\| (.+) \|$/gm, (match) => {
      const cells = match.split('|').filter((c) => c.trim());
      const isHeader = cells.some((c) => c.includes('---'));
      if (isHeader) return '';
      return (
        '<tr>' +
        cells.map((c) => `<td class="border px-2 py-1">${c.trim()}</td>`).join('') +
        '</tr>'
      );
    })
    .replace(/(<tr>.*<\/tr>)/gs, '<table class="w-full border-collapse my-3 text-sm">$1</table>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/\n{2,}/g, '</p><p class="my-3">')
    .replace(/\n/g, '<br/>');

  html = '<p class="my-3">' + html + '</p>';
  html = html.replace(/<p class="my-3"><br\/>/g, '<p class="my-3">');
  html = html.replace(/<br\/><\/p>/g, '</p>');

  return html;
}

/** 把 input/output 转成单行预览文本，多模态内容显示为占位文字 */
export function formatIOPreview(value: unknown): string {
  return ioText(value).replace(/\s+/g, ' ').trim().slice(0, PREVIEW_MAX_CHARS)
}

const PREVIEW_MAX_CHARS = 500
const CONTENT_BLOCK_TYPES = new Set(['text', 'image', 'image_url', 'input_image', 'file', 'input_file', 'audio', 'input_audio', 'video', 'document'])

const isContentBlock = (part: unknown) =>
  !!part && typeof part === 'object' && CONTENT_BLOCK_TYPES.has(String((part as { type?: unknown }).type))

function contentText(raw: unknown): string {
  const parsed = parseContent(raw)
  return [parsed.text, ...uniqueParts(parsed.parts).map(partLabel)].filter(Boolean).join(' ')
}

function ioText(value: unknown, depth = 0): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return contentText(value)
  if (typeof value !== 'object' || depth > 4) return String(value)
  if (Array.isArray(value)) {
    if (value.length === 0) return ''
    if (value.some(isContentBlock)) return contentText(value)
    return ioText(value[value.length - 1], depth + 1)
  }
  const obj = value as Record<string, unknown>
  for (const key of ['messages', 'content', 'text', 'output', 'input', 'query', 'message']) {
    if (obj[key] !== undefined && obj[key] !== null) {
      const text = ioText(obj[key], depth + 1)
      if (text) return text
    }
  }
  return safeStringify(value, undefined, 200)
}
